import { safeStorage } from '../utils/safeStorage';

const RUNTIME_CONFIG_CACHE_KEY = 'gweffit_runtime_config_v1';
const SYSTEM_USER_ID_CACHE_KEY = 'SYSTEM_USER_ID';
const SYSTEM_USERNAME_FALLBACK = 'system';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

let runtimeConfigPromise = null;

function isValidUuid(value) {
  return UUID_RE.test(String(value || '').trim());
}

async function loadBundledRuntimeConfig() {
  try {
    const response = await fetch('/runtime-config.json', { cache: 'no-store' });
    if (!response.ok) return {};
    const data = await response.json();
    return data && typeof data === 'object' ? data : {};
  } catch {
    return {};
  }
}

export async function getRuntimeConfig() {
  if (runtimeConfigPromise) return runtimeConfigPromise;

  runtimeConfigPromise = (async () => {
    const envSystemUserId = String(process.env.REACT_APP_SYSTEM_USER_ID || '').trim();
    const envDebug = String(process.env.REACT_APP_DEBUG || '').trim().toLowerCase();
    const envAdminIdsRaw = String(process.env.REACT_APP_ADMIN_AUTH_USER_IDS || '').trim();
    const envName = String(process.env.REACT_APP_ENV || process.env.NODE_ENV || 'production').trim();
    const cached = safeStorage.get(RUNTIME_CONFIG_CACHE_KEY);
    const cachedConfig = cached ? JSON.parse(cached) : {};

    // Optional window-injected config (future-safe), then bundled file.
    const injectedConfig =
      typeof window !== 'undefined' && window.__RUNTIME_CONFIG__ && typeof window.__RUNTIME_CONFIG__ === 'object'
        ? window.__RUNTIME_CONFIG__
        : null;
    const fileConfig = injectedConfig || (await loadBundledRuntimeConfig());

    const config = {
      envName,
      debug:
        envDebug
          ? ['1', 'true', 'yes', 'on'].includes(envDebug)
          : Boolean(fileConfig?.debug ?? cachedConfig?.debug ?? false),
      systemUserId:
        envSystemUserId ||
        String(fileConfig?.systemUserId || '') ||
        String(cachedConfig?.systemUserId || ''),
      systemUsername:
        String(fileConfig?.systemUsername || cachedConfig?.systemUsername || SYSTEM_USERNAME_FALLBACK),
      adminAuthUserIds:
        (envAdminIdsRaw
          ? envAdminIdsRaw.split(',').map((s) => s.trim()).filter(Boolean)
          : Array.isArray(fileConfig?.adminAuthUserIds)
            ? fileConfig.adminAuthUserIds.map((s) => String(s).trim()).filter(Boolean)
            : Array.isArray(cachedConfig?.adminAuthUserIds)
              ? cachedConfig.adminAuthUserIds.map((s) => String(s).trim()).filter(Boolean)
              : []),
      source:
        envSystemUserId
          ? 'env'
          : fileConfig?.systemUserId
            ? 'runtime_file'
            : cachedConfig?.systemUserId
              ? 'cache'
              : 'none',
    };

    safeStorage.set(RUNTIME_CONFIG_CACHE_KEY, JSON.stringify(config));
    return config;
  })();

  return runtimeConfigPromise;
}

export async function resolveSystemUserId(supabaseClient) {
  const cfg = await getRuntimeConfig();
  const envPresent = Boolean(String(process.env.REACT_APP_SYSTEM_USER_ID || '').trim());

  if (isValidUuid(cfg.systemUserId)) {
    return {
      id: cfg.systemUserId,
      source: cfg.source === 'env' ? 'env' : 'runtime_file',
      envPresent,
      cacheUsed: false,
      dbLookupUsed: false,
      error: null,
    };
  }

  const cachedCandidate = safeStorage.get(SYSTEM_USER_ID_CACHE_KEY);
  if (isValidUuid(cachedCandidate)) {
    return {
      id: cachedCandidate,
      source: 'local_storage',
      envPresent,
      cacheUsed: true,
      dbLookupUsed: false,
      error: null,
    };
  }

  try {
    const { data, error } = await supabaseClient
      .from('users')
      .select('id')
      .eq('username', cfg.systemUsername || SYSTEM_USERNAME_FALLBACK)
      .limit(1);

    if (error) {
      return {
        id: null,
        source: 'db_lookup',
        envPresent,
        cacheUsed: false,
        dbLookupUsed: true,
        error: error.message || String(error),
      };
    }

    const dbId = Array.isArray(data) ? data[0]?.id : null;
    if (isValidUuid(dbId)) {
      safeStorage.set(SYSTEM_USER_ID_CACHE_KEY, dbId);
      return {
        id: dbId,
        source: 'db_lookup',
        envPresent,
        cacheUsed: false,
        dbLookupUsed: true,
        error: null,
      };
    }

    return {
      id: null,
      source: 'db_lookup',
      envPresent,
      cacheUsed: false,
      dbLookupUsed: true,
      error: `system_user_not_found:${cfg.systemUsername || SYSTEM_USERNAME_FALLBACK}`,
    };
  } catch (err) {
    return {
      id: null,
      source: 'db_lookup',
      envPresent,
      cacheUsed: false,
      dbLookupUsed: true,
      error: err?.message || String(err),
    };
  }
}
